import React, { useLayoutEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import i18n from "i18n"
import { Box } from "@mui/material"

import MetricCard from "./MetricCard"
import BarChartCard from "./BarChartCard"
import ClosedCertificationsTable from "./ClosedCertificationsTable"
import {
  OVERVIEW_CARDS_GRID_GAP,
  overviewCardsGridSx4,
} from "./constants/overviewGrid"
import { OVERVIEW_CHART_CARD_MIN_HEIGHT_PX } from "./OverviewCard"

const InvoicingSection = ({ data, isLoading }) => {
  const chartPanelRef = useRef(null)
  const [chartPanelHeight, setChartPanelHeight] = useState(null)

  useLayoutEffect(() => {
    const panel = chartPanelRef.current
    if (!panel) return undefined

    const updateHeight = () => {
      setChartPanelHeight(panel.getBoundingClientRect().height)
    }

    updateHeight()

    const resizeObserver = new ResizeObserver(() => {
      updateHeight()
    })
    resizeObserver.observe(panel)

    return () => resizeObserver.disconnect()
  }, [isLoading, data?.statusBarChart, data?.closedCertificationsRows])

  const cards = (data?.cards ?? []).map(card => (
    <Box key={card.id ?? card.title} sx={{ minWidth: 0 }}>
      <MetricCard {...card} loading={isLoading} />
    </Box>
  ))

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: OVERVIEW_CARDS_GRID_GAP,
      }}
    >
      <Box sx={overviewCardsGridSx4}>{cards}</Box>
      <Box
        sx={{
          display: "grid",
          gap: OVERVIEW_CARDS_GRID_GAP,
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            lg: "repeat(2, minmax(0, 1fr))",
          },
          alignItems: "stretch",
        }}
      >
        <Box
          ref={chartPanelRef}
          sx={{
            minWidth: 0,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <BarChartCard
            title={i18n.t("overview.invoicingAmountByStatusChartTitle")}
            infoText={i18n.t("overview.invoicingAmountByStatusChartTooltip")}
            dataSource={data?.statusBarChart ?? []}
            isLoading={isLoading}
            currency={data?.currency}
            pointColorMapping="color"
            categoryInversed
          />
        </Box>
        <Box
          sx={{
            minWidth: 0,
            minHeight: 0,
            height: chartPanelHeight ?? OVERVIEW_CHART_CARD_MIN_HEIGHT_PX,
            maxHeight: chartPanelHeight ?? OVERVIEW_CHART_CARD_MIN_HEIGHT_PX,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <ClosedCertificationsTable
            rows={data?.closedCertificationsRows ?? []}
            isLoading={isLoading}
            currency={data?.currency}
          />
        </Box>
      </Box>
    </Box>
  )
}

InvoicingSection.propTypes = {
  data: PropTypes.shape({
    currency: PropTypes.string,
    cards: PropTypes.arrayOf(PropTypes.object),
    statusBarChart: PropTypes.arrayOf(PropTypes.object),
    closedCertificationsRows: PropTypes.arrayOf(PropTypes.object),
  }),
  isLoading: PropTypes.bool.isRequired,
}

export default InvoicingSection
